import React, { useEffect, useRef, useState } from "react";
import "../styles/GalleryStyles.css";
import Logger from "../../../utils/Logger";
import { useApi } from "../../../service/ApiContext";
import { LocalStorage } from "../../../utils/Utils";
import axios from "axios";
import { useAlert } from "../../../components/AlertContextComponent";
import { useSelector } from "react-redux";
import { RootState } from "../../../store/store";

/**
 * Props definition for ScreenRecorder component
 */
interface ScreenRecorderProps {
  mediaType: string;
  onSubmit: (file: File, s3Url?: string) => void | Promise<void>;
  disabled?: boolean;
  program_id?: string;
  subject_id?: string;
  topic_id?: string;
  video_id?: string;
  program_name?: string;
  video_name?: string;
  subject_name?: string;
  topic_name?: string;
}

/** Maximum recording duration limit in seconds */
const MAX_DURATION = 30;

/**
 * ==============================================================================
 * WebM EBML Duration Header Fixer
 * ==============================================================================
 * MediaRecorder generates WebM chunks on-the-fly without writing the duration
 * metadata byte sequence into the container header. This helper scans the
 * array buffer for the Segment Info EBML block (ID: 0x1549A966) and patches or
 * injects the Float64 duration value (ID: 0x4489). This allows HTML5 video elements
 * to accurately scrub, seek, and render total timeline duration.
 * ==============================================================================
 */
const fixWebmDuration = async (
  blob: Blob,
  durationMs: number,
): Promise<Blob> => {
  try {
    const buffer = await blob.arrayBuffer();
    const bytes = new Uint8Array(buffer);

    // Step 1: Locate Segment Info EBML Element (ID: 0x15, 0x49, 0xA9, 0x66)
    let infoPos = -1;
    for (let i = 0; i < bytes.length - 4; i++) {
      if (
        bytes[i] === 0x15 &&
        bytes[i + 1] === 0x49 &&
        bytes[i + 2] === 0xa9 &&
        bytes[i + 3] === 0x66
      ) {
        infoPos = i;
        break;
      }
    }

    if (infoPos === -1) return blob; // Segment Info header not found, return original blob

    // Step 2: Search for existing Duration element (ID: 0x44, 0x89) inside Segment Info block
    let durationPos = -1;
    for (let i = infoPos; i < Math.min(infoPos + 500, bytes.length - 10); i++) {
      if (bytes[i] === 0x44 && bytes[i + 1] === 0x89) {
        durationPos = i;
        break;
      }
    }

    // Step 3: Overwrite existing Float64 duration if present
    if (durationPos !== -1) {
      const view = new DataView(buffer);
      const dataOffset = durationPos + 3;
      if (dataOffset + 8 <= buffer.byteLength) {
        view.setFloat64(dataOffset, durationMs, false);
        return new Blob([buffer], { type: blob.type });
      }
    }

    // Step 4: Inject 11-byte Duration EBML Element (ID: 0x4489, Size: 0x88, Float64: durationMs)
    const durationElem = new Uint8Array(11);
    durationElem[0] = 0x44;
    durationElem[1] = 0x89;
    durationElem[2] = 0x88;
    const view = new DataView(durationElem.buffer);
    view.setFloat64(3, durationMs, false);

    const insertPos = infoPos + 8;
    if (insertPos <= bytes.length) {
      const newBytes = new Uint8Array(bytes.length + durationElem.length);
      newBytes.set(bytes.subarray(0, insertPos), 0);
      newBytes.set(durationElem, insertPos);
      newBytes.set(bytes.subarray(insertPos), insertPos + durationElem.length);
      return new Blob([newBytes], { type: blob.type });
    }
  } catch (e) {
    Logger.error("Error patching WebM duration header:", e);
  }
  return blob;
};

/**
 * ==============================================================================
 * ScreenRecorder Component
 * ==============================================================================
 * Provides 30-second screen capture functionality:
 * 1. Prompts browser display media permission via navigator.mediaDevices.getDisplayMedia.
 * 2. Streams live screen view & renders 30fps frames on a 2D canvas context.
 * 3. Captures & mixes both system/tab audio and microphone voice audio via Web Audio API.
 * 4. Enforces auto-stop at exactly 30 seconds.
 * 5. Generates an in-memory preview Blob URL for verification before uploading.
 * 6. Handles direct-to-S3 binary upload & gallery metadata registration.
 * ==============================================================================
 */
const ScreenRecorder: React.FC<ScreenRecorderProps> = ({
  onSubmit,
  mediaType,
  disabled = false,
  program_id,
  subject_id,
  topic_id,
  video_id,
  program_name,
  video_name,
  subject_name,
  topic_name,
}) => {
  // DOM Refs for live video, preview player, canvas rendering, and media streams
  const videoRef = useRef<HTMLVideoElement>(null);
  const previewVideoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const canvasStreamRef = useRef<MediaStream | null>(null);
  const drawAnimationRef = useRef<number | null>(null);

  // Audio Context Ref for combining System Audio + Microphone Voice
  const audioContextRef = useRef<AudioContext | null>(null);

  // Recording & timer control refs
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const activeStreamRef = useRef<MediaStream | null>(null);
  const previewUrlRef = useRef<string | null>(null);
  const startTimeRef = useRef<number>(0);

  // Component state definitions
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [permissionGranted, setPermissionGranted] = useState<boolean>(false);
  const [permissionError, setPermissionError] = useState<string | null>(null);
  const [loadingScreen, setLoadingScreen] = useState<boolean>(false);

  // Recording progress & preview states
  const [isRecording, setIsRecording] = useState<boolean>(false);
  const [seconds, setSeconds] = useState<number>(0);
  const [recordedBlob, setRecordedBlob] = useState<Blob | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);
  const [uploadSuccess, setUploadSuccess] = useState<boolean>(false);

  // Redux & API context hooks
  const programs = useSelector(
    (state: RootState) => state.events.selectedValues,
  );
  const selectedProgramId = programs?.[0]?.program_id || "";
  const selectedProgramName = programs?.[0]?.program_title || "";

  const api = useApi();
  const { showAlert } = useAlert();

  /**
   * Returns supported WebM video mimeType including audio codecs (opus) based on browser capability
   */
  const getSupportedMimeType = () => {
    const mimeTypes = [
      "video/webm;codecs=vp9,opus",
      "video/webm;codecs=vp8,opus",
      "video/webm;codecs=h264,opus",
      "video/webm",
      "video/mp4",
    ];
    for (const type of mimeTypes) {
      if (MediaRecorder.isTypeSupported(type)) {
        return type;
      }
    }
    return "";
  };

  /**
   * Combines system/tab audio from getDisplayMedia and microphone voice audio from getUserMedia
   * into a unified MediaStream via Web Audio API (AudioContext).
   */
  const combineAudioAndVideoStream = async (
    displayStream: MediaStream,
  ): Promise<MediaStream> => {
    const combinedStream = new MediaStream();

    // Add video track from display stream
    displayStream.getVideoTracks().forEach((track) => combinedStream.addTrack(track));

    // Try capturing microphone audio voice stream
    let micStream: MediaStream | null = null;
    try {
      micStream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
        },
        video: false,
      });
    } catch (e) {
      Logger.info("Microphone permission skipped or unavailable for screen record:", e);
    }

    const displayAudioTracks = displayStream.getAudioTracks();
    const micAudioTracks = micStream ? micStream.getAudioTracks() : [];

    // Mix audio tracks if present
    if (displayAudioTracks.length > 0 || micAudioTracks.length > 0) {
      try {
        const AudioCtx = window.AudioContext || (window as any).webkitAudioContext;
        if (AudioCtx) {
          const audioCtx = new AudioCtx();
          audioContextRef.current = audioCtx;
          const dest = audioCtx.createMediaStreamDestination();

          if (displayAudioTracks.length > 0) {
            const displaySource = audioCtx.createMediaStreamSource(
              new MediaStream(displayAudioTracks),
            );
            displaySource.connect(dest);
          }

          if (micAudioTracks.length > 0) {
            const micSource = audioCtx.createMediaStreamSource(
              new MediaStream(micAudioTracks),
            );
            micSource.connect(dest);
          }

          dest.stream.getAudioTracks().forEach((track) => combinedStream.addTrack(track));
        } else {
          displayAudioTracks.forEach((track) => combinedStream.addTrack(track));
          micAudioTracks.forEach((track) => combinedStream.addTrack(track));
        }
      } catch (e) {
        displayAudioTracks.forEach((track) => combinedStream.addTrack(track));
        micAudioTracks.forEach((track) => combinedStream.addTrack(track));
      }
    }

    return combinedStream;
  };

  /**
   * Creates a 30fps canvas stream from live video feed and attaches audio tracks.
   */
  const createCanvasStream = (streamWithAudio: MediaStream) => {
    if (!videoRef.current || !canvasRef.current) return null;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    if (!ctx) return null;

    const width = videoRef.current.videoWidth || 1280;
    const height = videoRef.current.videoHeight || 720;
    canvas.width = width;
    canvas.height = height;

    // Animation loop drawing video frames to canvas
    const draw = () => {
      if (videoRef.current) {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(videoRef.current, 0, 0, canvas.width, canvas.height);
      }
      drawAnimationRef.current = requestAnimationFrame(draw);
    };

    draw();

    // Capture 30fps stream from canvas
    const canvasStream = canvas.captureStream(30);

    // Attach all audio tracks (system audio + microphone voice) to canvas stream
    streamWithAudio.getAudioTracks().forEach((track) => {
      canvasStream.addTrack(track);
    });

    canvasStreamRef.current = canvasStream;
    return canvasStream;
  };

  /**
   * STEP 1: Prompt user for screen recording permission & initiate capture
   */
  const startScreenShareAndRecord = async () => {
    setLoadingScreen(true);
    setPermissionError(null);
    setRecordedBlob(null);
    setPreviewUrl(null);

    // Clean up any existing media streams
    if (stream) {
      stream.getTracks().forEach((track) => track.stop());
      setStream(null);
    }
    if (audioContextRef.current) {
      audioContextRef.current.close().catch(() => {});
      audioContextRef.current = null;
    }

    try {
      // Trigger native browser permission dialog (getDisplayMedia)
      let displayStream: MediaStream;
      try {
        displayStream = await navigator.mediaDevices.getDisplayMedia({
          video: true,
          audio: true,
        });
      } catch (err: any) {
        // Fallback to video-only if system audio is denied
        displayStream = await navigator.mediaDevices.getDisplayMedia({
          video: true,
          audio: false,
        });
      }

      // Combine system audio + microphone audio into unified stream
      const combinedStream = await combineAudioAndVideoStream(displayStream);

      setStream(combinedStream);
      setPermissionGranted(true);

      // Attach stream to live video element
      if (videoRef.current) {
        videoRef.current.srcObject = combinedStream;
        videoRef.current.muted = true;
        await videoRef.current.play().catch(() => {});
      }

      // Automatically handle user stopping screen share via browser floating bar ("Stop sharing")
      const videoTrack = displayStream.getVideoTracks()[0];
      if (videoTrack) {
        videoTrack.onended = () => {
          stopRecording();
        };
      }

      // Execute MediaRecorder start with combined stream
      executeStartRecording(combinedStream, displayStream);
    } catch (error: any) {
      setPermissionGranted(false);
      if (
        error.name === "NotAllowedError" ||
        error.name === "PermissionDeniedError"
      ) {
        setPermissionError(
          "Screen recording permission was denied or cancelled by user.",
        );
      } else {
        setPermissionError(
          `Unable to access screen recording: ${error.message || "Unknown error"}`,
        );
      }
    } finally {
      setLoadingScreen(false);
    }
  };

  /**
   * STEP 2: Execute MediaRecorder streaming & chunk collection in memory
   */
  const executeStartRecording = (
    combinedStream: MediaStream,
    displayStream: MediaStream,
  ) => {
    chunksRef.current = [];
    startTimeRef.current = Date.now();
    const mimeType = getSupportedMimeType();

    try {
      const recordingStream = createCanvasStream(combinedStream) || combinedStream;
      const options = mimeType ? { mimeType } : undefined;
      const recorder = new MediaRecorder(recordingStream, options);
      mediaRecorderRef.current = recorder;

      // Collect WebM binary chunks every 100ms
      recorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) {
          chunksRef.current.push(event.data);
        }
      };

      // Handle recording completion (assemble in-memory Blob & patch EBML duration header)
      recorder.onstop = async () => {
        const rawBlob = new Blob(chunksRef.current, { type: mimeType || "video/webm" });
        const durationMs = Math.max(1000, Date.now() - startTimeRef.current);
        const fixedBlob = await fixWebmDuration(rawBlob, durationMs);
        const url = URL.createObjectURL(fixedBlob);

        setRecordedBlob(fixedBlob);
        setPreviewUrl(url);

        // Cancel frame drawing loop
        if (drawAnimationRef.current) {
          cancelAnimationFrame(drawAnimationRef.current);
          drawAnimationRef.current = null;
        }

        // Close audio context
        if (audioContextRef.current) {
          audioContextRef.current.close().catch(() => {});
          audioContextRef.current = null;
        }

        // Stop canvas and display stream tracks
        canvasStreamRef.current?.getTracks().forEach((track) => track.stop());
        canvasStreamRef.current = null;

        combinedStream.getTracks().forEach((track) => track.stop());
        displayStream.getTracks().forEach((track) => track.stop());
        setStream(null);
      };

      // Start recorder with 100ms timeslice
      recorder.start(100);
      setSeconds(0);
      setIsRecording(true);
    } catch (e: any) {
      Logger.error("Failed to start MediaRecorder for screen:", e);
      setPermissionError("Failed to record screen stream.");
    }
  };

  /**
   * STEP 3: Stop active screen recording & clear timer
   */
  const stopRecording = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
      try {
        mediaRecorderRef.current.stop();
      } catch (e) {
        Logger.error("Error stopping recorder:", e);
      }
    }
    setIsRecording(false);
  };

  /**
   * STEP 4: Strict 30-Second Countdown & Auto-Stop Effect
   */
  useEffect(() => {
    if (!isRecording) return;

    timerRef.current = setInterval(() => {
      setSeconds((prev) => {
        const nextSec = prev + 1;
        if (nextSec >= MAX_DURATION) {
          if (timerRef.current) {
            clearInterval(timerRef.current);
            timerRef.current = null;
          }
          // Force automatic stop at exactly 30 seconds
          setTimeout(() => {
            stopRecording();
          }, 0);
          return MAX_DURATION;
        }
        return nextSec;
      });
    }, 1000);

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [isRecording]);

  /**
   * Handles user resetting preview and recording again
   */
  const handleRecordAgain = () => {
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
    setPreviewUrl(null);
    setRecordedBlob(null);
    setSeconds(0);
    setPermissionGranted(false);
    startScreenShareAndRecord();
  };

  /**
   * STEP 5: Submit recorded video file to S3 presigned URL & save to gallery backend
   */
  const submitVideo = async () => {
    if (!recordedBlob) return;

    setUploadProgress(0);
    setUploadSuccess(false);

    try {
      const file = new File(
        [recordedBlob],
        `screen-record-${Date.now()}.webm`,
        { type: recordedBlob.type || "video/webm" },
      );

      api.setAuthToken(LocalStorage.getLocalData("access_token"));

      // 1. Get presigned upload URL from API
      const presignedRes = await api.get<{
        uploadURL: string;
        key: string;
        downloadURL: string;
      }>(`/create_upload_url?file_type=video`);

      const { uploadURL, key, downloadURL } = presignedRes.data;

      // 2. Upload binary payload directly to AWS S3 bucket
      await axios.put(uploadURL, file, {
        headers: {
          "Content-Type": file.type,
        },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / (progressEvent.total || 1),
          );
          setUploadProgress(Math.min(99, percentCompleted));
        },
      });

      // 3. Register uploaded media metadata with gallery backend
      await api.post(`/gallery_upload`, {
        url: downloadURL,
        file_type: "video",
        program_id: program_id || selectedProgramId || "",
        subject_id: subject_id || "",
        topic_id: topic_id || "",
        video_id: video_id || "",
        program_name: program_name || selectedProgramName || "",
        video_name: video_name || "",
        subject_name: subject_name || "",
        topic_name: topic_name || "",
      });

      setUploadProgress(100);
      setUploadSuccess(true);

      setTimeout(async () => {
        await onSubmit(file, downloadURL);

        setPreviewUrl(null);
        setRecordedBlob(null);
        setSeconds(0);
        setUploadProgress(null);
        setUploadSuccess(false);
        setPermissionGranted(false);
      }, 1200);
    } catch (error) {
      Logger.error("Error uploading screen recording:", error);
      setUploadProgress(null);
      setUploadSuccess(false);
      showAlert({
        show: true,
        title: "Upload Failed",
        message: "Failed to upload screen recording. Please try again.",
        alertType: "error",
        onlyNeedOneButton: true,
        button1Text: "OK",
        btn1onPressed: () => {},
      });
    }
  };

  /**
   * Formats seconds into MM:SS format
   */
  const formatTime = (totalSeconds: number) => {
    const mins = Math.floor(totalSeconds / 60);
    const secs = totalSeconds % 60;
    return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  };

  // Sync refs for unmount cleanup
  useEffect(() => {
    activeStreamRef.current = stream;
  }, [stream]);

  useEffect(() => {
    previewUrlRef.current = previewUrl;
  }, [previewUrl]);

  // Clean up media tracks, AudioContext & object URLs on unmount
  useEffect(() => {
    return () => {
      if (activeStreamRef.current) {
        activeStreamRef.current.getTracks().forEach((track) => track.stop());
      }
      if (audioContextRef.current) {
        audioContextRef.current.close().catch(() => {});
      }
      if (previewUrlRef.current) {
        URL.revokeObjectURL(previewUrlRef.current);
      }
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
      if (drawAnimationRef.current) {
        cancelAnimationFrame(drawAnimationRef.current);
      }
    };
  }, []);

  // Bind live stream to video element during active recording
  useEffect(() => {
    if (videoRef.current && stream && !previewUrl) {
      videoRef.current.srcObject = stream;
      videoRef.current.play().catch(() => {});
    }
  }, [stream, previewUrl]);

  return (
    <div className="capture_section">
      <div className="capture_card">
        {/* Header Title & Subtitle */}
        <div className="capture_header">
          <h3>Screen Recording</h3>
          <p>
            Record your screen or active window for up to 30 seconds. The system will ask for permission and automatically stop after 30 seconds.
          </p>
        </div>

        {/* Viewport Container */}
        <div className="camera_container">
          {/* Hidden Canvas for 30fps Frame Extraction */}
          <canvas
            ref={canvasRef}
            width={1280}
            height={720}
            style={{ display: "none" }}
          />

          {/* Initial State: Permission Request Card */}
          {!previewUrl && !permissionGranted ? (
            <div className="permission_card" style={{ background: "transparent", border: "none", boxShadow: "none" }}>
              <div className="permission_icon_container" style={{ fontSize: "42px" }}>🖥️</div>
              <h3 style={{ marginTop: "12px", color: "#64748b" }}>Record Screen for 30 Seconds</h3>
              <p style={{ color: "#64748b", maxWidth: "440px", margin: "8px auto 20px auto" }}>
                Click " Record Screen " to grant permission to share your screen. Recording will start immediately and automatically stop after 30 seconds.
              </p>
              {permissionError && (
                <div
                  style={{
                    color: "#ef4444",
                    fontSize: "13px",
                    marginBottom: "16px",
                    fontWeight: 500,
                  }}
                >
                  ⚠️ {permissionError}
                </div>
              )}
              <button
                className="primary_btn"
                onClick={startScreenShareAndRecord}
                disabled={disabled || loadingScreen}
                style={{
                  padding: "12px 24px",
                  fontSize: "15px",
                  fontWeight: 600,
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                {loadingScreen ? "Requesting Permission..." : "🖥️ Record Screen"}
              </button>
            </div>
          ) : (
            <>
              {/* Active Recording vs Preview Video Player */}
              {!previewUrl ? (
                <video
                  ref={videoRef}
                  autoPlay
                  playsInline
                  muted
                  className="screen_preview"
                  style={{ objectFit: "contain", background: "#0f172a", transform: "none" }}
                />
              ) : (
                <video
                  ref={previewVideoRef}
                  key={previewUrl}
                  src={previewUrl}
                  controls
                  preload="auto"
                  playsInline
                  className="captured_preview no-mirror"
                  style={{ objectFit: "contain", background: "#0f172a" }}
                  onLoadedMetadata={() => {
                    if (previewVideoRef.current) {
                      const vid = previewVideoRef.current;
                      if (vid.duration === Infinity || isNaN(vid.duration)) {
                        vid.currentTime = 1e101;
                        vid.ontimeupdate = () => {
                          vid.ontimeupdate = null;
                          vid.currentTime = 0;
                        };
                      }
                    }
                  }}
                />
              )}

              {/* Live Recording HUD Overlay */}
              {!previewUrl && isRecording && (
                <div className="recording_hud">
                  <div className="recording_badge">
                    <span className="recording_dot"></span>
                    SCREEN RECORDING
                  </div>
                  <div className="record_hud_timer">
                    {formatTime(seconds)} / {formatTime(MAX_DURATION)}
                  </div>
                </div>
              )}

              {/* Progress Bar indicating 30s limit */}
              {!previewUrl && isRecording && (
                <div className="video_progress_container">
                  <div
                    className="video_progress_bar"
                    style={{ width: `${(seconds / MAX_DURATION) * 100}%` }}
                  ></div>
                </div>
              )}

              {/* Upload Progress Overlay */}
              {uploadProgress !== null && (
                <div className="camera_overlay_uploading">
                  {!uploadSuccess ? (
                    <>
                      <div className="spinner"></div>
                      <div
                        style={{
                          fontWeight: 600,
                          fontSize: "16px",
                          letterSpacing: "0.2px",
                        }}
                      >
                        Uploading Screen Recording... ({uploadProgress}%)
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="upload_success_checkmark">✓</div>
                      <div
                        style={{
                          fontWeight: 700,
                          fontSize: "18px",
                          color: "#10b981",
                        }}
                      >
                        Screen Recording Uploaded!
                      </div>
                    </>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        {/* Action Controls Row */}
        {permissionGranted && (
          <div className="capture_controls_row" style={{ justifyContent: "flex-end" }}>
            <div className="capture_actions">
              {!previewUrl ? (
                isRecording && (
                  <button className="danger_btn" onClick={stopRecording}>
                    Stop Recording Early
                  </button>
                )
              ) : (
                <>
                  <button
                    className="secondary_btn"
                    onClick={handleRecordAgain}
                    disabled={uploadProgress !== null}
                  >
                    Record Again
                  </button>
                  <button
                    className="success_btn"
                    onClick={submitVideo}
                    disabled={uploadProgress !== null}
                  >
                    Submit to Gallery
                  </button>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ScreenRecorder;
